import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Alert from 'react-bootstrap/Alert';
import CompanyInfoStep from './CompanyInfoStep';
import ServiceAreasStep from './ServiceAreasStep';
import ServicesProvidedStep from './ServicesProvidedStep';
import CompanyReviewStep from './CompanyReviewStep';
import { fetchCsrfToken } from '../../api/subcontractor';
import { submitCompanyRegistration } from '../../api/company';
import './CompanyRegistration.css';

const STEPS = [
  { id: 1, title: 'Company Information' },
  { id: 2, title: 'Service Areas' },
  { id: 3, title: 'Services Provided' },
  { id: 4, title: 'Review & Submit' },
];

const initialFormData = {
  // Company Information
  company_name: '',
  company_type: '',
  tax_id: '',
  website: '',
  year_established: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  // Primary Contact
  contact_first_name: '',
  contact_last_name: '',
  contact_title: '',
  contact_email: '',
  contact_phone: '',
  // Service Areas
  service_areas: [],
  // Services Needed
  services_provided: {},
  estimated_annual_volume: '',
  typical_project_size: '',
  // Additional
  additional_notes: '',
  how_heard_about_us: '',
};

function CompanyRegistration() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    fetchCsrfToken().catch(console.error);
  }, []);

  const updateFormData = (data) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setErrors({});
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.company_name.trim()) newErrors.company_name = 'Company name is required';
      if (!formData.company_type) newErrors.company_type = 'Company type is required';
      if (!formData.address.trim()) newErrors.address = 'Address is required';
      if (!formData.city.trim()) newErrors.city = 'City is required';
      if (!formData.state.trim()) newErrors.state = 'State is required';
      if (!formData.zip_code.trim()) newErrors.zip_code = 'ZIP code is required';
      if (!formData.contact_first_name.trim()) newErrors.contact_first_name = 'Contact first name is required';
      if (!formData.contact_last_name.trim()) newErrors.contact_last_name = 'Contact last name is required';
      if (!formData.contact_email.trim()) {
        newErrors.contact_email = 'Contact email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
        newErrors.contact_email = 'Invalid email format';
      }
      if (!formData.contact_phone.trim()) newErrors.contact_phone = 'Contact phone is required';
    }

    if (step === 2) {
      if (formData.service_areas.length === 0) {
        newErrors.service_areas = 'Please select at least one service area';
      }
    }

    if (step === 3) {
      const hasService = Object.values(formData.services_provided).some((s) => s.needed);
      if (!hasService) {
        newErrors.services_provided = 'Please select at least one service';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const submissionData = {
        ...formData,
        year_established: formData.year_established ? parseInt(formData.year_established, 10) : null,
        services_provided: Object.fromEntries(
          Object.entries(formData.services_provided)
            .filter(([, value]) => value.needed)
            .map(([key, value]) => [key, { priority: value.priority, details: value.details }])
        ),
      };

      await submitCompanyRegistration(submissionData);
      setSubmitSuccess(true);
    } catch (error) {
      setSubmitError(error.message || 'Failed to submit registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartOver = () => {
    setFormData(initialFormData);
    setCurrentStep(1);
    setSubmitSuccess(false);
    setSubmitError(null);
    setErrors({});
    setTermsAccepted(false);
  };

  const progress = (currentStep / STEPS.length) * 100;

  if (submitSuccess) {
    return (
      <Container className="py-4">
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <div className="text-success mb-3">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </div>
            <h3 className="mb-3">Registration Submitted Successfully!</h3>
            <p className="text-muted mb-4">
              Thank you for registering your company. We will review your application and contact you soon.
            </p>
            <button className="btn btn-primary" onClick={handleStartOver}>
              Submit Another Registration
            </button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Helmet>
        <title>Company Registration | Find Tree Care Subcontractors | United Arborist</title>
        <meta name="description" content="Register your tree service company with United Arborist for free. Find skilled subcontractors including tree climbers, equipment operators, and CDL drivers available nationwide." />
        <meta name="keywords" content="tree service company registration, find tree care subcontractors, hire arborist contractors, tree company subcontractors, tree service staffing, find tree climbers, hire equipment operators, tree crew staffing" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.unitedarborist.com/company-registration" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="United Arborist" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content="Company Registration | Find Tree Care Subcontractors | United Arborist" />
        <meta property="og:description" content="Register your tree service company with United Arborist to find skilled subcontractors including climbers, equipment operators, and CDL drivers." />
        <meta property="og:url" content="https://www.unitedarborist.com/company-registration" />
        <meta property="og:image" content="https://www.unitedarborist.com/logo.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Company Registration | Find Tree Care Subcontractors | United Arborist" />
        <meta name="twitter:description" content="Register your tree service company with United Arborist to find skilled subcontractors including climbers, equipment operators, and CDL drivers." />
        <meta name="twitter:image" content="https://www.unitedarborist.com/logo.jpg" />
      </Helmet>
      <Card className="shadow-sm">
        <Card.Header className="bg-white">
          <h4 className="mb-3">Company Registration</h4>
          <div className="step-indicators d-none d-md-flex justify-content-between mb-3">
            {STEPS.map((step) => (
              <div
                key={step.id}
                className={`step-indicator ${currentStep >= step.id ? 'active' : ''} ${
                  currentStep === step.id ? 'current' : ''
                }`}
              >
                <span className="step-number">{step.id}</span>
                <span className="step-title">{step.title}</span>
              </div>
            ))}
          </div>
          <div className="d-md-none mb-2">
            <small className="text-muted">
              Step {currentStep} of {STEPS.length}: {STEPS[currentStep - 1].title}
            </small>
          </div>
          <ProgressBar now={progress} className="mb-0" style={{ height: '4px' }} />
        </Card.Header>

        <Card.Body>
          {submitError && (
            <Alert variant="danger" dismissible onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          {currentStep === 1 && (
            <CompanyInfoStep
              data={formData}
              errors={errors}
              onChange={updateFormData}
            />
          )}

          {currentStep === 2 && (
            <ServiceAreasStep
              selectedStates={formData.service_areas}
              error={errors.service_areas}
              onChange={(areas) => updateFormData({ service_areas: areas })}
            />
          )}

          {currentStep === 3 && (
            <ServicesProvidedStep
              data={formData}
              errors={errors}
              onChange={updateFormData}
            />
          )}

          {currentStep === 4 && (
            <CompanyReviewStep
              data={formData}
              termsAccepted={termsAccepted}
              onTermsChange={setTermsAccepted}
            />
          )}
        </Card.Body>

        <Card.Footer className="bg-white">
          <div className="d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handlePrevious}
              disabled={currentStep === 1}
            >
              Previous
            </button>

            {currentStep < STEPS.length ? (
              <button type="button" className="btn btn-primary" onClick={handleNext}>
                Next
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-success"
                onClick={handleSubmit}
                disabled={isSubmitting || !termsAccepted}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Registration'}
              </button>
            )}
          </div>
        </Card.Footer>
      </Card>
    </Container>
  );
}

export default CompanyRegistration;
